import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, OnDestroy, OnInit, untracked, ViewChild } from '@angular/core';
import { MagicSearchService } from '../../../../../creators/service/magic-search/magic-search.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { FilterDrawerComponent } from '@/app/pages/creators-v4-filter/molecules/filter-drawer/filter-drawer.component';
import { FilterItemDto } from '@/app/common/app-config/filter-models/misc';
import { BlueGoldSearchItemComponent } from '@/app/pages/creators-v4-filter/molecules/blue-gold-search-item/blue-gold-search-item.component';
import { ManageBriefTabsMonitorService } from '../../../../services/manage-brief-tabs-monitor/manage-brief-tabs-monitor';
import {
  formatFollowerRangeLabel,
  FollowerRangeValue,
} from '@/app/common/app-config/filter-models/follower-range';

@Component({
  selector: 'mc-search-creators',
  imports: [FilterDrawerComponent, BlueGoldSearchItemComponent],
  templateUrl: './search-creators.html',
  styleUrl: './search-creators.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SearchCreators implements OnInit, OnDestroy {

  readonly monitoringMode = input(false);

  @ViewChild(FilterDrawerComponent) private filterDrawer?: FilterDrawerComponent;

  private keywordInput$ = new Subject<string>();
  private subscriptions = new Subscription();
  private isPaginating = false;

  /** Services */
  public magicSearchService = inject(MagicSearchService);
  public manageBriefTabsMonitorService = inject(ManageBriefTabsMonitorService);
  /** Services - END */

  constructor() {
    effect(() => {
      this.monitoringMode();
      this.manageBriefTabsMonitorService.focussedMonitoringBrief();
      untracked(() => {
        this.syncSocialMonitoringBriefContext();
      });
    });
  }

  ngOnInit(): void {
    this.syncSocialMonitoringBriefContext();

    const initFilters = {
      ...this.magicSearchService.appliedFilters,
    };

    this.magicSearchService.appliedFilters = initFilters;
    this.magicSearchService.kickOffBlueSearch(initFilters);

    const keywordSub = this.keywordInput$
      .pipe(debounceTime(300))
      .subscribe((value) => {
        const trimmed = value?.trim() ?? '';
        this.magicSearchService.searchQuery.set(trimmed);

        this.magicSearchService.filtersObjectSignal.update(current => {
          const updated = { ...current } as any;
          if (trimmed.length > 0) {
            updated.searchTerm = trimmed;
          } else {
            delete updated.searchTerm;
          }
          return updated;
        });

        const updatedFilters = {
          ...this.magicSearchService.appliedFilters,
          searchTerm: trimmed.length > 0 ? trimmed.toLowerCase() : undefined,
        };

        this.magicSearchService.appliedFilters = updatedFilters;
        this.magicSearchService.kickOffBlueSearch(updatedFilters);
      });

    this.subscriptions.add(keywordSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.magicSearchService.socialMonitoringBriefIdContext.set(undefined);
  }

  /** Same idea as collaborators briefId on creators-v4-filter — all blue searches merge this id on the service. */
  private syncSocialMonitoringBriefContext(): void {
    if (!this.monitoringMode()) {
      this.magicSearchService.socialMonitoringBriefIdContext.set(undefined);
      return;
    }

    const id = this.manageBriefTabsMonitorService.focussedMonitoringBrief()?.id;
    this.magicSearchService.socialMonitoringBriefIdContext.set(id);
  }

  onKeywordSearchInput(value: string): void {
    this.keywordInput$.next(value ?? '');
  }

  removeFilter(filter: string) {
    this.magicSearchService.filtersObjectSignal.update(current => {
      const updated = { ...current } as any;
      delete updated[filter];
      return updated;
    });

    if (filter === 'searchTerm') {
      this.magicSearchService.clearKeywordSearch();
    }

    this.magicSearchService.appliedFilters = {
      ...this.magicSearchService.appliedFilters,
      [filter]: undefined
    };

    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters);
  }

  getInterestsLabel(interests: unknown): string {
    const list = interests as { name?: string }[] | undefined;
    if (!list || !Array.isArray(list) || list.length === 0) {
      return '';
    }
    return list
      .map(item => item?.name || '')
      .filter(name => name && name.length > 0)
      .join(', ');
  }

  public hasActiveFilters(): boolean {
    const filters = this.magicSearchService.appliedFilters;
    if (!filters) {
      return false;
    }
    return Object.keys(filters).length > 0;
  }

  removeSortFilter(): void {
    this.filterDrawer?.clearSortAndRepublish();
  }

  formatFollowerRange(range: FollowerRangeValue): string {
    return formatFollowerRangeLabel(range);
  }

  removeFollowerRangeFilter(platform: 'tiktok' | 'instagram' | 'youtube' | 'snapchat'): void {
    this.filterDrawer?.clearFollowerRangeAndRepublish(platform);
  }

  removeAgeFilter() {
    this.magicSearchService.filtersObjectSignal.update(current => {
      const updated = { ...current } as any;
      delete updated.ageOptions;
      return updated;
    });

    this.magicSearchService.appliedFilters = {
      ...this.magicSearchService.appliedFilters,
      ageOptions: undefined
    };

    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters);
  }

  toolbarSortSelectValue(): string {
    const sort = (this.magicSearchService.filtersObjectSignal() as { sort?: FilterItemDto }).sort;
    if (sort?.id !== undefined && sort.id !== null && `${sort.id}` !== '') {
      return String(sort.id);
    }
    return '1';
  }

  isToolbarSortOptionSelected(sortOption: FilterItemDto): boolean {
    return String(sortOption.id) === this.toolbarSortSelectValue();
  }

  toolbarSortDisplayName(): string {
    const fromSignal = (this.magicSearchService.filtersObjectSignal() as { sort?: FilterItemDto }).sort;
    if (fromSignal?.name) {
      return fromSignal.name;
    }
    const idVal = this.toolbarSortSelectValue();
    const opts =
      this.magicSearchService.fancyActiveFilters?.sortOptions ??
      this.magicSearchService.activeFilters.sortOptions;
    const match = opts?.find((o) => String(o.id) === idVal);
    return match?.name ?? 'Sort';
  }

  onToolbarSortOptionSelected(sortOption: FilterItemDto): void {
    this.filterDrawer?.applySortFromToolbar(sortOption);
  }

  onResultsScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const threshold = 100;
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (!atBottom) {
      return;
    }

    if (this.isPaginating || this.magicSearchService.isSearching()) {
      return;
    }

    if (!this.magicSearchService.blueSearchResults() || this.magicSearchService.blueSearchResults()!.length === 0) {
      return;
    }

    this.isPaginating = true;

    this.magicSearchService.pageNumber.update(current => current + 1);
    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters, true);

    setTimeout(() => {
      this.isPaginating = false;
    }, 500);
  }
}
